import { useRef, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';

// ========================================
// TYPES
// ========================================

interface SignaturePadProps {
  onSignatureChange: (dataURL: string) => void;
  onClear: () => void;
  disabled?: boolean;
}

// ========================================
// COMPONENTE SIGNATURE PAD
// ========================================

const SignaturePad = ({ onSignatureChange, onClear, disabled = false }: SignaturePadProps) => {
  const sigCanvasRef = useRef<SignatureCanvas>(null);
  const [isEmpty, setIsEmpty] = useState<boolean>(true);
  const [canvasSize, setCanvasSize] = useState({ width: 600, height: 200 });

  // Ajustar tamaño del canvas según el viewport (responsive)
  useEffect(() => {
    const updateCanvasSize = () => {
      const container = document.getElementById('signature-canvas-container');
      if (container) {
        const width = container.offsetWidth;
        const height = width < 640 ? 160 : 200; // Más bajo en móvil
        setCanvasSize({ width, height });
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  /**
   * Limpiar firma del canvas
   */
  const handleClear = () => {
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
      setIsEmpty(true);
      onClear();
    }
  };

  /**
   * Handler cuando empieza a dibujar
   */
  const handleBeginStroke = () => {
    if (!disabled) {
      setIsEmpty(false);
    }
  };

  /**
   * Handler cuando termina un trazo
   */
  const handleEndStroke = () => {
    if (sigCanvasRef.current && !disabled) {
      const dataURL = sigCanvasRef.current.toDataURL('image/png');
      onSignatureChange(dataURL);
    }
  };

  return (
    <div className="w-full">
      {/* Canvas Container */}
      <div 
        id="signature-canvas-container"
        className={`border-2 rounded-lg bg-white relative ${
          disabled 
            ? 'border-gray-300 bg-gray-50 cursor-not-allowed' 
            : 'border-gray-400 hover:border-blue-500 transition-colors'
        }`}
      >
        <SignatureCanvas
          ref={sigCanvasRef}
          penColor={disabled ? '#ccc' : '#000'}
          canvasProps={{
            width: canvasSize.width,
            height: canvasSize.height,
            className: `w-full ${disabled ? 'cursor-not-allowed' : 'cursor-crosshair touch-none'}`
          }}
          onBegin={handleBeginStroke}
          onEnd={handleEndStroke}
          dotSize={disabled ? 0 : undefined}
        />
        
        {/* Placeholder cuando está vacío */}
        {isEmpty && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-gray-400 text-xs sm:text-sm text-center px-4">
              {disabled 
                ? 'Firma deshabilitada'
                : '✍️ Firme aquí con su dedo o stylus'
              }
            </p>
          </div>
        )}
      </div>

      {/* Botón Limpiar */}
      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={handleClear}
          disabled={isEmpty || disabled}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            isEmpty || disabled
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-red-500 text-white hover:bg-red-600 active:bg-red-700'
          }`}
        >
          🗑️ Limpiar Firma
        </button>
      </div>

      {/* Ayuda */}
      <p className="mt-2 text-xs text-gray-500 text-center">
        {disabled 
          ? 'Complete los campos requeridos para habilitar la firma'
          : 'Su firma se guardará automáticamente al firmar el documento'
        }
      </p>
    </div>
  );
};

export default SignaturePad;
